import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { buildQueryString } from "@mimber-client/utils"
import {
  GroupMemberWithRelationsDto,
  ResponseDto,
  QueryParamsDto,
} from "@mimber/schema"
import { memberGroupsKeys } from "../member-groups.utils"

/**
 * Hook for fetching groups a member belongs to
 * Can only be used in mimber-organization app
 *
 * @param memberUuid - Member UUID
 * @param params - Query parameters (pagination, sorting, filtering)
 * @param options - React Query options
 */
export const useMemberGroups = (
  memberUuid: string,
  params?: QueryParamsDto,
  options?: UseQueryOptions<ResponseDto<GroupMemberWithRelationsDto[]>, Error>
): UseQueryResult<ResponseDto<GroupMemberWithRelationsDto[]>, Error> => {
  guardAppUsage("mimber-organization", "useMemberGroups")

  const { server } = useServerContext()

  return useQuery({
    queryKey: memberGroupsKeys.findMemberGroups(memberUuid, params),
    queryFn: async () => {
      const response = await server.get<
        ResponseDto<GroupMemberWithRelationsDto[]>
      >(buildQueryString(`members/${memberUuid}/groups`, params))
      return response.data
    },
    staleTime: 5 * 60 * 1000, // Consider member groups data fresh for 5 minutes
    enabled: !!memberUuid, // Only run query if memberUuid is provided
    ...options,
  })
}
